import React, { FC, useEffect, ChangeEvent, useCallback, useState, FormEvent } from 'react';
import styled from 'styled-components';
import { useTranslation, Trans } from 'react-i18next';
import {
  TextInput,
  Option,
  Button,
  ButtonSize,
  ButtonColor,
  FileUpload,
} from '@code/innovance-react-ui-kit';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';

import { isInvalidFileError } from 'src/front/common/utils/errorUtils';
import { PTSDocumentUploadSchema } from 'src/front/common/utils/validationSchemas';
import { ACCEPTED_FILE_UPLOAD_FORMATS } from 'src/front/common/constants/files';
import { checkAcceptedFileType, openDocument } from 'src/front/common/utils/fileUploadUtils';
import { formatAlphaNumericCyrillicSpecialCharactersWithoutQuotes } from 'src/front/common/utils/formUtils';

import { ModelError } from 'src/front/api/generated';

import FormModal from 'src/front/components//FormModal';
import previewIcon from 'src/front/assets/icon_preview.svg';
import { ResetButton, FormElement, Title } from 'src/front/components/FormStyles';

export const FormRow = styled(FormElement)`
  margin: 10px 0;
`;

export const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;

export const StyledResetButton = styled(ResetButton)`
  margin-right: 20px;
`;
export const StyledPreviewButton = styled.button`
  position: relative;

  border: none;
  margin-right: 20px;

  background: none;

  &&:focus {
    outline: none;
  }
`;

export interface UploadDocumentFormData {
  documentType: Option | null;
  files?: FileList | null;
  filePath?: string;
  explanation: string;
  isOldDocument: boolean;
  createDate: string;
  documentId: string;
  documentName: string;
}

const defaultValues = {
  documentType: null,
  files: undefined,
  filePath: '',
  explanation: '',
  isOldDocument: false,
  createDate: '',
  documentId: '',
  documentName: '',
};

interface Props {
  onClose: () => void;
  submit: (data: UploadDocumentFormData) => void;
  formValues?: UploadDocumentFormData;
  error?: ModelError;
  defaultExplanation?: string;
}

const UploadDocumentModal: FC<Props> = ({
  formValues,
  onClose,
  submit,
  defaultExplanation,
  error,
}) => {
  const { t } = useTranslation();
  const [previewAvailable, setPreviewAvailable] = useState<boolean>(false);

  const {
    watch,
    register,
    errors,
    setValue,
    getValues,
    handleSubmit,
    control,
    reset,
    formState,
    setError,
  } = useForm<UploadDocumentFormData>({
    defaultValues: {
      ...defaultValues,
      ...formValues,
    },
    resolver: yupResolver(PTSDocumentUploadSchema),
  });

  const registerFields = useCallback(() => {
    register({ name: 'files' });
    register({ name: 'filePath' });
    register({ name: 'isOldDocument' });
    register({ name: 'documentId' });
  }, [register]);

  useEffect(() => {
    if (error && isInvalidFileError(error)) {
      setError('files', { type: 'required', message: 'general_uploadFileFormat_errorMessage' });
    }
  }, [error, setError]);

  useEffect(() => {
    registerFields();
  }, [registerFields]);

  const handleFileSelect = ({ target }: ChangeEvent<HTMLInputElement>): void => {
    setValue('files', target.files, { shouldValidate: true, shouldDirty: true });
    setValue('filePath', target.value);
    setPreviewConfiguration(target.files);
  };
  const onReset = (): void => {
    reset();
    registerFields();
  };

  const onPreview = (): void => {
    const { files } = getValues();
    openDocument(files);
  };

  const setPreviewConfiguration = async (data?: FileList | null): Promise<void> => {
    const acceptStatus = await checkAcceptedFileType(data);
    setPreviewAvailable(acceptStatus);
  };

  const onSubmit = async (data: UploadDocumentFormData): Promise<void> => {
    await submit({ ...formValues, ...data });
  };

  const handleFormSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    event.stopPropagation();
  };

  const isEditMode = Boolean(formValues?.documentId);

  return (
    <FormModal onClose={onClose} zIndex={800}>
      <Title>
        <Trans i18nKey="general_components_documentUpload_text" />
      </Title>
      <form onSubmit={handleFormSubmit}>
        <FormRow>
          <FileUpload
            accept={ACCEPTED_FILE_UPLOAD_FORMATS}
            description={t('general_uploadFile_text')}
            disabled={isEditMode}
            errorMessage={t(errors?.files?.message as string)}
            label={t('general_components_uploadFile_label')}
            name="file"
            onChange={handleFileSelect}
            value={watch('filePath')}
            required
          >
            {previewAvailable ? (
              <StyledPreviewButton onClick={onPreview} type="button">
                <img alt="previewIcon" src={previewIcon} />
              </StyledPreviewButton>
            ) : null}
          </FileUpload>
        </FormRow>
        <FormRow>
          <Controller
            control={control}
            name="explanation"
            render={({ onChange, value }) => (
              <TextInput
                disabled={Boolean(defaultExplanation)}
                errorMessage={t(errors?.explanation?.message as string)}
                formatter={formatAlphaNumericCyrillicSpecialCharactersWithoutQuotes}
                label={t('general_comment_label')}
                maxLength={100}
                name="explanation"
                onChange={onChange}
                value={defaultExplanation || value}
              />
            )}
          />
        </FormRow>
        <ButtonRow>
          {formState.isDirty ? (
            <StyledResetButton
              color={ButtonColor.Secondary}
              onClick={onReset}
              size={ButtonSize.Small}
              text={t('general_components_reset_button')}
              type="reset"
            />
          ) : null}
          <Button
            color={ButtonColor.Primary}
            disabled={formState.isSubmitting}
            onClick={handleSubmit(onSubmit)}
            size={ButtonSize.Medium}
            text={t('general_save_button')}
            type="button"
          />
        </ButtonRow>
      </form>
    </FormModal>
  );
};

export default UploadDocumentModal;
